<template>
  <slot v-if="promotionSignRequired">
    <JointLoanLineLimitIncreaseBanner :amount="newCreditLimit" />
  </slot>
  <slot v-else>
    <!--////////////////////////////RESULT////////////////////////////-->
    <div class="main" v-if="showResult">
      <Result
        :icon="getIcon('img/credit-line/success_icon.png')"
        :title="resultTitle"
        :description="resultDescription"
        :secondDescription="resultDescription2"
        :btn-text="resultButtonText"
        @dismissFunc="dismiss()"
      />
    </div>
    <main class="home" v-else>
      <!--////////////////////////////CARD BANNER////////////////////////////-->
      <RequestCardBanner
        :open="openRequestCardBanner && !loading"
        :dismissable="cardBannerDismissable"
        :should-have-card-in-hand="shouldHaveCardInHand"
        @close="openRequestCardBanner = false"
      />
      <div
        class="header-container"
        v-if="!openRequestCardBanner && !showErrorResult"
      >
        <Header
          v-if="!(currentStep === Steps.INSUFFICIENT_FUNDS && newUI)"
          :navbarType="navbarType"
          :screenTitle="screenTitle"
          :show-help-btn="currentStep === Steps.INSUFFICIENT_FUNDS"
          :show-progress-bar="
            bankTransferEnable &&
            currentStep !== Steps.INSUFFICIENT_FUNDS &&
            !showErrorResult
          "
          :progresPercentage="progressPercentage"
          @clicked="goBack"
        />
      </div>
      <!--////////////////////////////WITHDRAWAL FLOW////////////////////////////-->
      <div
        class="pb-0 mt-4 overflow-hidden w-100"
        style="top: 95px"
        v-if="!openRequestCardBanner"
      >
        <InsufficientWithdrawalFunds
          v-if="currentStep === Steps.INSUFFICIENT_FUNDS"
          :available-credit="Number(maxAmount)"
          :minimum-withdrawal="Number(minAmount)"
          :currency-symbol="currencySymbol"
        />
        <SelectWithdrawMethod
          v-if="currentStep === Steps.SELECT_METHOD"
          class="step-component"
          :enabled-withdrawal="enabledWithdrawal"
          :bank-account-info="bankAccountInfo || undefined"
          :transfer-flag="flagBankTransfer"
          :show-bank-transfer-enable="bankTransferEnable"
          :window-flag="flagWindowPickup"
          :window-withdrawal="windowWithdrawal?.enabled"
          :display-notice="displayNotice"
          :card-withdrawal="cardWithdrawal?.enabled"
          :card-title="selectWithdrawCardTitle"
          :card-message="selectWithdrawCardMessage"
          :card-description-text="cardDescriptionText"
          @select-method="handleWithdrawMethodSelection"
          @set-screen-title="setScreenTitle"
          @edit-account-number="handleEditAccountClick"
          @unlink-account="handleUnlinkAccountClick"
        />
        <Result
          v-if="doesHaveEnoughBalanceToWithdraw"
          :icon="getIcon('img/credit-line/creditLineAdvise.png')"
          :title="underAvailableCreditTitle"
          :description="underAvailableCreditDescription"
          :secondDescription="underAvailableCreditDescription2"
        />
        <BankAccountSetup
          v-if="currentStep === Steps.ACCOUNT_SETUP"
          :editMode="editBankAccountMode"
          :windowPickupDisabledReason="windowPickUpDisableReason"
          @next="
            ($event) => handleAccountSetupResult($event.clabe, $event.bank)
          "
          @setScreenTitle="setScreenTitle"
          class="step-component"
        />
        <UnlinkBankAccount
          v-if="currentStep === Steps.UNLINK_ACCOUNT"
          :clabeNumber="bankAccountInfo?.clabe"
          @next="handleUnlinkConfirmClick"
          @setScreenTitle="setScreenTitle"
          class="step-component"
        />
        <IdentityVerification
          v-if="currentStep === Steps.IDENTITY_VERIFICATION"
          @next="($event) => handleCurpValidationResult($event?.curp)"
          @setLoading="(isLoading) => setLoading(isLoading)"
          @setScreenTitle="setScreenTitle"
          :require-validation-response="requiresIdentityResponse"
          :customerName="customerName"
          :hide-close-button="true"
          class="step-component"
          :user-flow="identityValidationReference as KycUserFlow"
        />
        <KycFailed v-if="showErrorResult" />
        <NewAccountDetails
          v-if="currentStep === Steps.REVIEW_NEW_ACCOUNT"
          class="step-component"
          :clabe="FormatBankAccountHelper(newClabeValue)"
          :bank="newBankValue"
          :curp="customerCurpValue"
          :show-alert="showAlert"
          :alert-message="bankAccountErrorMessage"
          :requiresIdentityVerification="identityVerificationSettingEnabled"
          @next="handleNewAccountSubmit"
          @setScreenTitle="setScreenTitle"
          @close-alert="showAlert = false"
        />
        <Withdraw
          v-if="currentStep === Steps.SELECT_WITHDRAW_AMOUNT"
          class="step-component"
          @set-screen-title="setScreenTitle"
          :step="noBankTransferStep"
          :onlineTransfer="withdrawMethod === WithdrawMethod.ONLINE"
          :bank-account-info="bankAccountInfo || undefined"
          @setLoading="(isLoading) => setLoading(isLoading)"
          @setPercentage="setProgressPercentage"
          @updateStep="updateNbtStep"
          :max-amount="Number(maxAmount)"
          :min-amount="Number(minAmount)"
          :userId="userId"
          :aml="amlSurvey"
          @show-return="setShowResult"
          @withdraw-data="handleSecureWithdraw"
          :require-validation-response="requiresIdentityResponse"
          :customerName="customerName"
          :validation-reference="identityValidationReference"
        />
        <AntiMoneyLaunderingSurvey
          v-if="currentStep === Steps.AML_SURVEY"
          @on-continue-click="saveSurvey"
        />
      </div>
    </main>
    <div v-if="loading" class="loading-overlay">
      <i class="fa fa-spin fa-spinner" />
    </div>
  </slot>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onMounted,
  ref,
  watch,
  provide,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import Withdraw from "@/apps/credit-line/components/Withdraw.vue";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import Header from "@/shared-components/consumer/Header.vue";
import SelectWithdrawMethod from "@/apps/credit-line/components/Withdrawal/SelectWithdrawMethod.vue";
import UnlinkBankAccount from "@/apps/credit-line/components/Withdrawal/UnlinkBankAccount.vue";
import BankAccountSetup from "@/apps/credit-line/components/Withdrawal/BankAccountSetup.vue";
import IdentityVerification from "@/apps/credit-line/components/Withdrawal/IdentityVerification.vue";
import NewAccountDetails from "@/apps/credit-line/components/Withdrawal/NewAccountDetails.vue";
import {
  AccountValidationStatus,
  IBankAccountInfo,
} from "@/apps/credit-line/models/IBankAccountInfo";
import {
  WithdrawalStatusValues,
  CustomerType,
} from "@/apps/credit-line/models/CreditLineDetails";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { Service } from "@/apps/credit-line/services/network.service";
import {
  ErrorHandler as ClErrorHandler,
  ErrorHandler,
} from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { IBankOptionInfo } from "@/apps/credit-line/models/IBankOptionInfo";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { FormatBankAccountHelper } from "@/apps/credit-line/helpers/formatBankAccount.helper";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import AntiMoneyLaunderingSurvey from "@/apps/credit-line/components/Withdrawal/AntiMoneyLaunderingSurvey.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import Result from "@/apps/credit-line/components/Result.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import { AxiosError } from "axios";
import InsufficientWithdrawalFunds from "@/apps/credit-line/components/Withdrawal/InsufficientFunds.vue";
import RequestCardBanner from "@/apps/credit-line/components/Withdrawal/RequestCardBanner.vue";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { getPrivacyPolicyUrl } from "@/apps/credit-line/helpers/privacyPolicy.helper";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import {
  DEFAULT_SETTING,
  SettingValue,
} from "@/shared-models/consumer/ISetting";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import {
  useGeolocation,
  GeolocationErrorResponse,
  GeolocationSuccessResponse,
} from "@/apps/credit-line/composables/useGeolocationFromNative";
import { areValidationRequired } from "../../services/kyc";
import { KycUserFlow } from "../../enums/kycEnums";
import KycFailed from "@/apps/credit-line/components/KycValidations/KycFailed.vue";
import { store } from "@/shared-store";
import JointLoanLineLimitIncreaseBanner from "@/apps/credit-line/components/Offers/JointLoanLineLimitIncreaseBanner.vue";
import { useNewLimitIncreaseOffer } from "@/apps/credit-line/hooks/useNewLimitIncreaseOffer";
import { AreValidationsRequiredResponse } from "../../models/IKycValidations";
import { AMLSettings } from "@/apps/credit-line/helpers/aml-settings.helper.ts";

declare const Android: AndroidInterface;

// Set language before mount
setLanguage(getCurrentInstance()?.proxy);

enum Steps {
  SELECT_METHOD = 1,
  ACCOUNT_SETUP,
  IDENTITY_VERIFICATION,
  UNLINK_ACCOUNT,
  REVIEW_NEW_ACCOUNT,
  SELECT_WITHDRAW_AMOUNT,
  AML_SURVEY,
  INSUFFICIENT_FUNDS,
  NOTHING,
}

type WithdrawMethodSelectionPayload = {
  method: WithdrawMethod;
  shouldAdvance: boolean;
};

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const navbarType = ref<string>("goBack");
const screenTitle = ref<string>("");
const progressPercentage = ref<number>(15);
const currentStep = ref<number>(0);
const apiService = new Service();
const bankAccountInfo = ref<IBankAccountInfo | null>(null);
const creditLineId = ref<number>(0);
const customerName = ref<string>("");
const withdrawMethod = ref<WithdrawMethod>();
const lastStep = ref();
const newClabeValue = ref<string>("");
const newBankValue = ref<IBankOptionInfo>();
const customerCurpValue = ref<string>("");
const loading = ref<boolean>(false);
const editBankAccountMode = ref<boolean>(false);
const windowPickUpDisableReason = ref<string>();
const bankTransferEnable = ref<boolean>(false);
const requiresIdentityResponse = ref<AreValidationsRequiredResponse | null>(
  null,
);
const noBankTransferStep = ref<string>();
const showAlert = ref<boolean>(false);
const app = getCurrentInstance()?.appContext.app;
const userId = ref<string>("");
const amlSurvey = ref<boolean>();
const showResult = ref<boolean>(false);
const secureWithdrawalParams = ref();
const resultTitle = ref<string>("");
const resultDescription = ref<string>("");
const resultDescription2 = ref<string>("");
const resultButtonText = ref<string>("");
const displayNotice = ref<boolean>(false);
const selectWithdrawCardTitle = ref<string>("");
const selectWithdrawCardMessage = ref<string>("");
const identityValidationReference = ref<string>("");
const maxAmount = ref<number>(0);
const minAmount = ref<number>(0);
const country = ref<CL_COUNTRY>();
const countryCl = ref<CL_COUNTRY>();
const currencySymbol = ref<string>("$");
const openRequestCardBanner = ref<boolean>(false);
const cardBannerDismissable = ref<boolean>(true);
const bankAccountErrorMessage = ref("");
const availableCredit = ref<number>(0);
const showErrorResult = ref(false);
const enabledWithdrawal = ref<boolean>();
const geolocation = ref<GeolocationSuccessResponse | null>(null);
// Methods flags (tied to FEV2)
const flagBankTransfer = ref<boolean>(false);
const flagWindowPickup = ref<boolean>(false);
// Settings
const identityVerificationSettingEnabled = ref<boolean>(true);
const hardOneFunnelSetting = ref<SettingValue>(DEFAULT_SETTING);
const withdrawalFunnelSettings = ref<SettingValue>(DEFAULT_SETTING);
const cardWithdrawal = ref<SettingValue>(DEFAULT_SETTING);
const windowWithdrawal = ref<SettingValue>(DEFAULT_SETTING);
const bankTransferWithdrawal = ref<SettingValue>(DEFAULT_SETTING);
const geoLocationRequired = ref<SettingValue>(DEFAULT_SETTING);
const spCashPickupExp = ref<SettingValue>(DEFAULT_SETTING);
const unFreezingWithdrawals = ref<boolean | undefined>(false);
const cardDescriptionText = ref<string>(t("CL_WITHDRAWAL.CARD_DESCRIPTION"));
const newInputWithdrawalAmountUi = ref<boolean>(false);
const amlSettings = ref<AMLSettings | null>(null);

provide("amlSettings", amlSettings);
provide("withdrawalGeolocation", geolocation);

const underAvailableCreditTitle = t(
  "CL_WITHDRAWAL.UNDER_AVAILABLE_CREDIT_TITLE",
);
const underAvailableCreditDescription = t(
  "CL_WITHDRAWAL.UNDER_AVAILABLE_CREDIT_DESCRIPTION_1",
);
const underAvailableCreditDescription2 = t(
  "CL_WITHDRAWAL.UNDER_AVAILABLE_CREDIT_DESCRIPTION_2",
  {
    availableCredit: availableCredit,
  },
);

const customerType = ref<CustomerType | "">("");

// Computed properties
const newUI = computed(() => typeof Android.shouldDisplayNewUi() === "boolean");
const shouldHaveCardInHand = computed(
  () =>
    customerType.value === CustomerType.SIMULTANEOUS_LOAN_LINE ||
    customerType.value === CustomerType.KIOSK,
);
const { fetchSummary, doesHaveEnoughBalanceToWithdraw } =
  useCreditLineSummary();
const { newCreditLimit, promotionSignRequired } = useNewLimitIncreaseOffer();

// Methods
const getIcon = (path: string): string => {
  return useAsset(path);
};

const areValidationsRequired = async () => {
  try {
    const response = await areValidationRequired({
      creditLineId: creditLineId.value,
      userFlow: identityValidationReference.value,
      deviceTag: AuthenticationHelper.getDeviceTag(),
    });
    store.commit("creditLine/SET_WITHDRAWAL_VALIDATION", response.data);
    if (!response.data.wasSuccessful) {
      ClErrorHandler.logCreditlineErrorResponse(response);
      setTimeout(() => {
        showErrorResult.value = true;
        loading.value = false;
        currentStep.value = Steps.NOTHING;
      }, 1000);
      return;
    }
    if (response.data.wasSuccessful) {
      requiresIdentityResponse.value = response.data;
      return;
    }
  } catch (err) {
    ErrorHandler.handleNetworkErrorAndLog(err as AxiosError);
  }
};

const setProgressPercentage = (val: number) => (progressPercentage.value = val);

function getSummary(force = false) {
  fetchSummary(force)
    .then((summary) => {
      // Set basic summary data
      customerType.value = summary.customerType;
      creditLineId.value = summary.creditLineId;
      availableCredit.value = summary?.availableCredit;
      currencySymbol.value = summary?.currencySymbol;

      // Set country and amounts
      country.value = summary.country?.toUpperCase() as CL_COUNTRY;
      countryCl.value = summary.country?.toUpperCase() as CL_COUNTRY;
      maxAmount.value = summary?.availableCredit;
      minAmount.value = summary?.minimumWithdrawalAmount ?? 0;

      // Set withdrawal conditions
      const withdrawalConditions = summary?.withdrawalConditions;
      flagBankTransfer.value = withdrawalConditions?.flagBankTransfer ?? false;
      flagWindowPickup.value = withdrawalConditions?.flagWindowPickup ?? false;
      windowPickUpDisableReason.value =
        withdrawalConditions?.windowPickupDisabledReason;

      // Handle Hard One Funnel experiment
      let partOfHardOneFunnel: boolean;

      partOfHardOneFunnel = hardOneFunnelSetting.value?.enabled;

      if (
        hardOneFunnelSetting.value?.bucket &&
        hardOneFunnelSetting.value?.bucket === 0
      ) {
        partOfHardOneFunnel = false;
      }

      const isEligibleForCard =
        summary.creditCardStatus === PayJoyCardStatus.ELIGIBLE &&
        summary.shipments?.length === 0;

      if (partOfHardOneFunnel && isEligibleForCard) {
        cardBannerDismissable.value = false;
        openRequestCardBanner.value = true;
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.UndismissableWithdrawCardPromo,
          "PayJoy Card",
          "Undismissable card promo on withdrawal screen",
        );
        return;
      }

      // Handle Super Pac-Man cash pickup experiment
      const partOfSpCashPickupExp =
        spCashPickupExp.value?.enabled &&
        Boolean(spCashPickupExp.value?.bucket);

      if (partOfSpCashPickupExp) {
        flagWindowPickup.value = !flagWindowPickup.value;
      }

      // Set bank transfer enable status
      bankTransferEnable.value = !!(
        summary?.isBankTransferEnabled && bankTransferWithdrawal.value?.enabled
      );

      // Check if user has enough balance to withdraw
      if (Number(maxAmount.value) < Number(minAmount.value)) {
        currentStep.value = Steps.INSUFFICIENT_FUNDS;
        navbarType.value = "initial";
        return;
      }

      // Handle card banner for eligible users
      if (isEligibleForCard) {
        openRequestCardBanner.value = true;
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.WithdrawCardPromo,
          "PayJoy Card",
          "Regular card promo on withdrawal screen",
        );
      }

      // Handle bank transfer setup
      if (bankTransferEnable.value) {
        if (summary?.bankAccountInfo) {
          bankAccountInfo.value = summary.bankAccountInfo;
        }
        if (summary?.customerName) {
          customerName.value = summary.customerName;
        }
      } else {
        noBankTransferStep.value = "chooseAmount";
      }

      // Handle AML Survey requirement
      if (
        summary?.userId &&
        withdrawalFunnelSettings.value?.values?.amlSurveyOnFirstWithdrawal
      ) {
        userId.value = summary.userId.toString();
        requireAmlSurvey(summary.creditLineId);
      }

      // Handle withdrawal restrictions
      const withdrawalRestricted =
        summary.withdrawalStatus !== WithdrawalStatusValues.ENABLED ||
        !withdrawalConditions?.withinBankOperationHours;

      if (withdrawalRestricted) {
        setSelectWithdrawCard(summary);
        if (withdrawalRestricted) {
          flagBankTransfer.value = false;
          flagWindowPickup.value = false;
        }
      }

      enabledWithdrawal.value =
        summary.withdrawalStatus === WithdrawalStatusValues.ENABLED;

      // Set current step after all processing
      currentStep.value = Steps.SELECT_METHOD;
    })
    .finally(() => {
      safeAndroidCall("finishedLoading");
    });
}

watch(currentStep, (n) => (progressPercentage.value = 15 * n));

const nextStep = (step: Steps) => {
  if (
    step === Steps.SELECT_WITHDRAW_AMOUNT &&
    newInputWithdrawalAmountUi.value
  ) {
    return router.push({
      name: "InputWithdrawalAmountView",
    });
  }

  currentStep.value = step;
};

const setScreenTitle = (title: string) => (screenTitle.value = title);

const setSelectWithdrawCard = (data) => {
  if (data.withdrawalStatus === WithdrawalStatusValues.FROZEN_PERMANENTLY) {
    selectWithdrawCardTitle.value = t(
      "CL_WITHDRAWAL.PERMANENTLY_FROZEN_WITHDRAWALS_TITLE",
    );
    selectWithdrawCardMessage.value = t(
      "CL_WITHDRAWAL.PERMANENTLY_FROZEN_WITHDRAWALS_MESSAGE",
    );
  }
  if (!data?.withdrawalConditions?.withinBankOperationHours) {
    selectWithdrawCardMessage.value = t(
      "CL_WITHDRAWAL.OUTSIDE_BANKING_HOURS_MESSAGE",
    );
  }

  if (data.withdrawalStatus === WithdrawalStatusValues.FROZEN) {
    selectWithdrawCardTitle.value = t("CL_WITHDRAWAL.FROZEN_WITHDRAWALS_TITLE");
    selectWithdrawCardMessage.value = t(
      "CL_WITHDRAWAL.FROZEN_WITHDRAWALS_MESSAGE",
    );
  }

  if (unFreezingWithdrawals.value) {
    selectWithdrawCardTitle.value = t("CL_WITHDRAWAL.DEFROST_CARD_TITLE");
    selectWithdrawCardMessage.value = t(
      "CL_WITHDRAWAL.DEFROST_CARD_DESCRIPTION",
    );
  }
  displayNotice.value = true;
};

const AUTO_ADVANCE_DELAY_MS = 1000;

const handleWithdrawMethodSelection = async ({
  method,
  shouldAdvance,
}: WithdrawMethodSelectionPayload) => {
  store.commit("creditLine/SET_SELECTED_WITHDRAW_METHOD", method);

  // Handle GeoLocation extraction if ONLINE method is selected and setting enabled
  // Block flow by returning early if geolocation fails
  if (method === WithdrawMethod.ONLINE && geoLocationRequired.value?.enabled) {
    try {
      const capturedGeolocation = await useGeolocation();
      geolocation.value = capturedGeolocation;
    } catch (error: unknown) {
      const geoError = error as GeolocationErrorResponse;
      console.error(geoError.errorType, geoError.error);
      return;
    }
  }

  // we set the reference based on the selected  TODO: Implement configurability per country to this
  switch (method) {
    case WithdrawMethod.ONLINE:
      identityValidationReference.value =
        KycUserFlow.creditLineOnlineWithdrawal;
      break;
    case WithdrawMethod.CASH:
      identityValidationReference.value = KycUserFlow.creditLineCashWithdrawal;
      break;
  }
  // then we ask if validations are required
  if (
    identityVerificationSettingEnabled.value &&
    identityValidationReference.value
  ) {
    areValidationsRequired();
  } else {
    requiresIdentityResponse.value = {
      requiredValidations: [],
      wasSuccessful: true,
    };
  }
  withdrawMethod.value = method;

  if (!shouldAdvance) {
    return;
  }

  const advanceToNextStep = () => {
    editBankAccountMode.value = false;
    if (method === WithdrawMethod.CARD) {
      void router.replace({
        name: "CreditCardView",
      });
      return;
    }
    if (method === WithdrawMethod.ONLINE) {
      const isValidBankAccount =
        bankAccountInfo?.value?.status === AccountValidationStatus.VALIDATED;
      if (isValidBankAccount) {
        const isValidationRequired =
          identityVerificationSettingEnabled.value &&
          Array.isArray(requiresIdentityResponse?.value?.requiredValidations) &&
          requiresIdentityResponse?.value?.requiredValidations?.length > 0;
        if (isValidationRequired) {
          nextStep(Steps.IDENTITY_VERIFICATION);
        } else {
          lastStep.value = Steps.SELECT_METHOD;
          setProgressPercentage(50);
          nextStep(Steps.SELECT_WITHDRAW_AMOUNT);
        }
        return;
      }

      nextStep(Steps.ACCOUNT_SETUP);
      return;
    }

    nextStep(Steps.SELECT_WITHDRAW_AMOUNT);
  };

  if (method !== WithdrawMethod.ONLINE) {
    setTimeout(advanceToNextStep, AUTO_ADVANCE_DELAY_MS);
    return;
  }

  advanceToNextStep();
};

const updateNbtStep = () => (noBankTransferStep.value = "details");

const goBack = (step?: Steps) => {
  showErrorResult.value = false;
  if (!bankTransferEnable.value) {
    if (noBankTransferStep.value === "chooseAmount") {
      safeAndroidCall("dismissWebView");
      safeAndroidCall("creditOfferAccepted");
    } else {
      noBankTransferStep.value = "chooseAmount";
    }
    return;
  }

  if (currentStep.value === Steps.SELECT_WITHDRAW_AMOUNT) {
    currentStep.value = Steps.SELECT_METHOD;
    return;
  } else {
    if (lastStep.value) {
      currentStep.value = lastStep.value;
      lastStep.value = currentStep.value - 1;
      return;
    }

    if (step) {
      currentStep.value = step;
      return;
    }
  }

  if (currentStep.value !== 1) {
    currentStep.value -= 1;
  } else {
    safeAndroidCall("dismissWebView");
    safeAndroidCall("creditOfferAccepted");
  }
};

const setLoading = (isLoading: boolean) => (loading.value = isLoading);

const handleAccountSetupResult = (clabe: string, bank: IBankOptionInfo) => {
  newClabeValue.value = clabe;
  newBankValue.value = bank;
  const needsValidation =
    requiresIdentityResponse.value &&
    requiresIdentityResponse.value?.requiredValidations.length > 0;
  nextStep(
    identityVerificationSettingEnabled.value === true && needsValidation
      ? Steps.IDENTITY_VERIFICATION
      : Steps.REVIEW_NEW_ACCOUNT,
  );
  // Make sure back button returns to main page if we're on the edit flow
  if (editBankAccountMode.value) {
    lastStep.value = Steps.SELECT_METHOD;
  }
};

const handleCurpValidationResult = (curp: string) => {
  customerCurpValue.value = curp;

  store.dispatch("creditLine/setCurpValidationResult", {
    status: "SUCCESS",
    attempts: 0,
    nationalId: curp,
  });
  store.dispatch("creditLine/setValidationSuccess", true);

  lastStep.value = Steps.IDENTITY_VERIFICATION;
  // Make sure edit flow is resumed after Curp validation
  if (editBankAccountMode.value) {
    nextStep(Steps.REVIEW_NEW_ACCOUNT);
    return;
  }

  if (bankAccountInfo?.value && bankAccountInfo.value.status === "VALIDATED") {
    nextStep(Steps.SELECT_WITHDRAW_AMOUNT);
  } else {
    nextStep(Steps.REVIEW_NEW_ACCOUNT); // If CURP validation success
  }
};

const handleNewAccountSubmit = () => {
  setLoading(true);
  apiService
    .validateBankAccount({
      counterpart_institution: newBankValue.value?.key ?? "",
      bankInstitution: newBankValue.value?.participantKey ?? "",
      beneficiary_account: newClabeValue.value,
      deviceTag: (safeAndroidCall("getDeviceTag") as string) || "",
    })
    .then(() => {
      router.push({
        name: "CreditLineBankAccountVerification",
      });
    })
    .catch((error: AxiosError) => {
      bankAccountErrorMessage.value = getErrorMessage(
        error.response?.data.errorCode,
        t,
      );
      showAlert.value = true;
      ErrorHandler.handleNetworkErrorAndLog(error);
    })
    .finally(() => {
      setLoading(false);
    });
};

const handleEditAccountClick = () => {
  nextStep(Steps.ACCOUNT_SETUP);
  editBankAccountMode.value = true;
};

const handleUnlinkAccountClick = () => {
  lastStep.value = Steps.SELECT_METHOD;
  nextStep(Steps.UNLINK_ACCOUNT);
};

const handleUnlinkConfirmClick = (clabe: string) => {
  loading.value = true;
  apiService
    .unlinkBankAccount({
      beneficiary_account: clabe,
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
    })
    .then((res) => {
      if (res.data.message === "Success") {
        router.push({
          name: "SurveyView",
          query: { surveyType: "BANK_ACCOUNT_UNLINK" },
        });
      } else {
        throw new Error(res.data.message || "API Error");
      }
    })
    .catch((err) => {
      bankAccountErrorMessage.value = getErrorMessage(
        err.response.data.errorCode,
        t,
      );
      showAlert.value = true;
      ErrorHandler.handleNetworkErrorAndLog(err);
    })
    .finally(() => {
      loading.value = false;
    });
};

watch(amlSurvey, (newValue) => {
  if (newValue) {
    getPrivacyPolicyUrl().then((res) => {
      const privacyPolicyUrl = res;
      resultTitle.value = t(
        "PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.WITHDRAW_RESULT_TITLE",
      );
      resultDescription.value = t(
        "PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.WITHDRAW_RESULT_DESCRIPTION",
      );
      resultDescription2.value =
        t(
          "PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.WITHDRAW_RESULT_SECOND_DESCRIPTION",
        ) +
        "<br><a class='link no-' href='" +
        privacyPolicyUrl +
        "' target='_blank'>" +
        t("PAYJOY_PRIVACY_POLICY") +
        "</a>";
      resultButtonText.value = t(
        "PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.WITHDRAW_RESULT_BUTTONTEXT",
      );
    });
  } else {
    resultTitle.value = t("CL_WITHDRAWAL.WITHDRAWAL_REQUEST_MSG");
    resultDescription.value = t("CL_WITHDRAWAL.WITHDRAWAL_REQUEST_DESCRIPTION");
    resultDescription2.value = "";
    resultButtonText.value = t("CL_WITHDRAWAL.GO_TO_HOME_BTN");
  }
});

const setShowResult = (newVal: boolean) => {
  showResult.value = newVal;
};

const saveSurvey = (data) => {
  data.userId = userId.value;
  data.deviceTag = AuthenticationHelper.getDeviceTag();
  data.creditLineId = creditLineId.value;
  apiService
    .SaveAmlSurvey(data)
    .then((res) => {
      if (res.data?.success) {
        // Only proceed with withdrawal if there's no blocking answer
        if (!data.hasBlockingAnswer) {
          secureWithdrawal();
          //router.push("/creditline/anti-money-laundering/result");
        }
      }
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    });
};

const dismiss = () => {
  if (amlSurvey.value) {
    showResult.value = false;
    currentStep.value = Steps.AML_SURVEY;
  } else {
    safeAndroidCall("confirmWithdrawal");
    safeAndroidCall("creditOfferAccepted");
  }
};

function requireAmlSurvey(creditLineId: number) {
  apiService
    .RequireAmlSurvey({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      creditLineId: creditLineId,
      userId: userId.value,
    })
    .then((res) => {
      const needSurvey = Boolean(res.data?.needSurvey);
      store.commit("creditLine/SET_AML_SURVEY_DATA", { needSurvey });
      amlSurvey.value = needSurvey;
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    });
}

function handleSecureWithdraw(params) {
  secureWithdrawalParams.value = params;
}

function secureWithdrawal() {
  apiService
    .secureWithdrawal(secureWithdrawalParams.value)
    .then((response) => {
      if (response.status === 200) {
        if (response.data.debitId) {
          amlSurvey.value = false;
          showResult.value = true;
          //router.push("/creditline/withdrawal-request");
        }
      }
    })
    .catch((error) => {
      const errorCode = getErrorMessage(
        (error.response &&
          error.response.data &&
          error.response.data.errorCode) ||
          705,
        t,
      );
      router.replace({
        name: "CreditLineApiErrorDisplay",
        query: {
          title: t("ERROR_SOMETHING_GOES_WRONG"),
          description: errorCode,
          returnHomeOnTryAgain: "true",
        },
      });
    });
}

const fetchSettings = async () => {
  try {
    const withdrawalSettings = await getSettings(
      SettingsCategory.CL_WITHDRAWAL,
    );

    const ws = withdrawalSettings || ({} as SettingValue[]);

    withdrawalFunnelSettings.value = ws.withdrawalFunnel || DEFAULT_SETTING;
    hardOneFunnelSetting.value = ws.hardOneFunnel || DEFAULT_SETTING;
    cardWithdrawal.value = ws.cardWithdrawal || DEFAULT_SETTING;

    // Exit early if lineProduct is fully disabled, or withdrawalFunnel is disabled at a high-level
    if (!ws.lineProduct?.enabled || !ws.withdrawalFunnel?.enabled) {
      void router.replace({ name: "UseYourCardView" });
      return;
    }

    geoLocationRequired.value =
      ws.withdrawalGeolocationRequired || DEFAULT_SETTING;

    const descriptionKey = ws.cardWithdrawal?.values?.descriptionTextKey;
    const cardActivationSettings = await getSettings(
      SettingsCategory.CL_CARDS_PAGE,
    );

    amlSettings.value = cardActivationSettings?.amlSurveyConfiguration?.values
      ?.formValuesRules as unknown as AMLSettings | null;
    cardDescriptionText.value =
      typeof descriptionKey === "string"
        ? t(descriptionKey)
        : t("CL_WITHDRAWAL.CARD_DESCRIPTION");

    windowWithdrawal.value = ws.windowWithdrawal || DEFAULT_SETTING;
    bankTransferWithdrawal.value =
      ws.withdrawalFunnel?.bankTransferWithdrawal ||
      ws.bankTransferWithdrawal ||
      DEFAULT_SETTING;
    spCashPickupExp.value = ws.spCashPickupExp || DEFAULT_SETTING;

    unFreezingWithdrawals.value = Boolean(
      ws.unfreezingCLWithdrawals?.enabled ||
      ws.unfreezingCLWithdrawalsByLatePayment?.enabled,
    );

    identityVerificationSettingEnabled.value = Boolean(
      withdrawalFunnelSettings.value?.values?.identityVerificationRequired,
    );

    newInputWithdrawalAmountUi.value = Boolean(
      ws.withdrawalAmountNewUi?.enabled,
    );

    if (ws.withdrawalEstimateFields?.enabled) {
      store.commit(
        "creditLine/SET_WITHDRAWAL_ESTIMATE_FIELDS",
        ws.withdrawalEstimateFields.values || null,
      );
    }
  } catch (error) {
    console.log(error);
  }
};

onMounted(async () => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.ClickWithdraw,
    "Happy Path",
    "Withdrawal Flow started",
  );
  recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
  recorder(app)?.updateRecordingInfo(getRandomNumberBetweenRange(1000000, 1), {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });

  await fetchSettings();

  void store.dispatch("creditLine/clearWithdrawalEstimate");
  void store.dispatch("creditLine/setCurpValidationResult", null);
  void store.dispatch("creditLine/setValidationSuccess", null);
  store.commit("creditLine/SET_WITHDRAWAL_VALIDATION", null);
  store.commit("creditLine/SET_SELECTED_WITHDRAW_METHOD", null);

  getSummary(route.query.reload === "true");

  if (
    bankTransferWithdrawal.value?.enabled &&
    geoLocationRequired.value?.enabled
  ) {
    await useGeolocation();
  }
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/constants";

.step-component {
  height: 100%;
  overflow-y: hidden;
  position: relative !important;
}

.header-container {
  width: 100%;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 200;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-green;
  font-size: 40px;
  background-color: rgba(150, 150, 150, 0.4);
  width: 100vw;
  height: 100%;
  z-index: 1000;
}

.link {
  color: $pj-color-green;
  font-weight: 600;
  clear: both;
  text-decoration: none;
  font-size: 16px;
}
</style>
